import { Flex } from "@mantine/core";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { twMerge } from "tailwind-merge";

import { ScreenerBuilderNavItem } from "./nav-item";
import { useScreenerBuilderEnabled } from "@/hooks/screener-builder-hooks";
import { useImpersonationBannerHeight } from "@/hooks/use-impersonation-banner-height";
import { ScreenerBuilderStep } from "@/types/screener-builder";
import { LAYOUT_DIMENSIONS } from "@/utils/ui";

export const SCREENER_BUILDER_NAV_WIDTH_PX = 180;

interface ScreenerBuilderNavigationProps {
  furthestProgressedStep: ScreenerBuilderStep;
  isLegacyScreener?: boolean;
}

export const ScreenerBuilderNavigation = ({
  furthestProgressedStep,
  isLegacyScreener = false,
}: ScreenerBuilderNavigationProps) => {
  const screenerBuilderEnabled = useScreenerBuilderEnabled();
  const shouldHideStepNumber = isLegacyScreener || !screenerBuilderEnabled;
  // While the types specify pathname is always a string, the docs mention edge
  // cases where it is null. Fallback to empty string for safety.
  const pathname = usePathname() ?? "";
  const activeStep = useMemo(() => {
    const pathParts = pathname.split("/")[3];

    switch (pathParts) {
      case "position":
        return ScreenerBuilderStep.Position;
      case "context":
        return ScreenerBuilderStep.Context;
      case "locations":
        return ScreenerBuilderStep.Locations;
      case "review":
        return ScreenerBuilderStep.Review;
      default:
        return ScreenerBuilderStep.Position;
    }
  }, [pathname]);
  const impersonationBannerHeight = useImpersonationBannerHeight();

  const locationNavItem = (
    <ScreenerBuilderNavItem
      activeStep={activeStep}
      disabled={
        isLegacyScreener
          ? false
          : [
              ScreenerBuilderStep.Position,
              ScreenerBuilderStep.Context,
            ].includes(furthestProgressedStep)
      }
      hideStepNumber={shouldHideStepNumber}
      step={ScreenerBuilderStep.Locations}
    />
  );

  const reviewNavItem = (
    <ScreenerBuilderNavItem
      activeStep={activeStep}
      hideStepNumber={shouldHideStepNumber}
      step={ScreenerBuilderStep.Review}
      label="Questions"
      disabled={
        isLegacyScreener
          ? false
          : [
              ScreenerBuilderStep.Position,
              ScreenerBuilderStep.Context,
              ScreenerBuilderStep.Locations,
            ].includes(furthestProgressedStep)
      }
    />
  );

  return (
    <Flex
      className={twMerge(["bg-white", "border-r", "border-uw-gray-2", "p-3"])}
      component="nav"
      style={{
        height: `calc(100dvh - ${
          LAYOUT_DIMENSIONS.combinedHeaderHeight + impersonationBannerHeight
        }px)`,
        minWidth: `${SCREENER_BUILDER_NAV_WIDTH_PX}px`,
        width: `${SCREENER_BUILDER_NAV_WIDTH_PX}px`,
      }}
    >
      <ol className="w-full">
        {isLegacyScreener ? (
          <>
            {locationNavItem}
            {reviewNavItem}
          </>
        ) : (
          <>
            <ScreenerBuilderNavItem
              activeStep={activeStep}
              hideStepNumber={shouldHideStepNumber}
              step={ScreenerBuilderStep.Position}
            />
            <ScreenerBuilderNavItem
              activeStep={activeStep}
              disabled={furthestProgressedStep === ScreenerBuilderStep.Position}
              hideStepNumber={shouldHideStepNumber}
              step={ScreenerBuilderStep.Context}
            />
            {locationNavItem}
            {reviewNavItem}
          </>
        )}
      </ol>
    </Flex>
  );
};
